import type { UseRadioProps } from '@chakra-ui/react';
import { Box, Flex, HStack, useRadio, useRadioGroup } from '@chakra-ui/react';
import { ReactNode, useEffect } from 'react';
import { theme } from '../../shared/theme';

type ResistomapSwitchOption<Value extends string> = {
  value: Value;
  label: string;
  tooltip?: ReactNode;
};

type ResistomapSwitchProps<Value extends string> = {
  options: readonly [ResistomapSwitchOption<Value>, ResistomapSwitchOption<Value>];
  value: Value;
  onChange: (value: Value) => void;
  isDisabled?: boolean;
  // Optional fixed widths (px) for left and right segments. Defaults preserve existing behavior.
  widths: readonly [number, number];
};

export function ResistomapSwitch<Value extends string>({
  options,
  value,
  onChange,
  isDisabled = false,
  widths,
}: ResistomapSwitchProps<Value>) {
  // Kept for future enabling of small screen mode
  // const screenSize = useBreakpointValue({ base: 'base', md: 'md', lg: 'lg', lg2: 'lg2', xl: 'xl' });
  // const useFullWidth = screenSize === 'xl' || screenSize === 'lg2' || screenSize === 'md' || screenSize === 'base';

  const {
    setValue,
    getRootProps,
    getRadioProps,
    value: currentValue,
  } = useRadioGroup({
    name: 'resistomap-switch',
    value,
    onChange: nextValue => {
      onChange(nextValue as Value);
    },
  });

  useEffect(() => {
    if (currentValue !== value) {
      setValue(value);
    }
  }, [currentValue, setValue, value]);

  const group = getRootProps();

  // Preserve previous hardcoded desktop widths if none provided
  const [leftWidth, rightWidth] = widths;

  return (
    <Flex align="center" bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
      <HStack
        {...group}
        borderRadius="md"
        height="38px"
        overflow="hidden"
        bg={theme.colors.neutral100}
        border={`1px solid ${theme.colors.neutral300}`}
        opacity={isDisabled ? 0.5 : 1}
      >
        {options.map((option, index) => {
          const radio = getRadioProps({ value: option.value });
          return (
            <RadioCard
              key={option.value}
              isDisabled={isDisabled}
              isBigScreen={true}
              position={index === 0 ? 'left' : index === options.length - 1 ? 'right' : 'center'}
              selectedValue={value}
              value={option.value}
              width={index === 0 ? leftWidth : rightWidth}
              {...radio}
            >
              {option.label}
            </RadioCard>
          );
        })}
      </HStack>
    </Flex>
  );
}

type RadioCardProps = UseRadioProps & {
  children: ReactNode;
  value: string;
  selectedValue: string;
  position: 'left' | 'right' | 'center';
  isDisabled: boolean;
  isBigScreen: boolean | undefined;
  width: number;
};

function RadioCard({
  children,
  value,
  selectedValue,
  position,
  isDisabled,
  isBigScreen: _isBigScreen,
  width,
  ...props
}: RadioCardProps) {
  const { getInputProps, getCheckboxProps, htmlProps } = useRadio({ ...props, value });
  const input = getInputProps();
  const checkbox = getCheckboxProps();
  const isSelected = value === selectedValue;

  const selectedProps = isSelected
    ? {
        bg: 'white',
        py: '2px',
        px: '10px',
        borderRadius: '5px',
        fontWeight: 500,
      }
    : {
        bg: theme.colors.neutral100,
        py: '10px',
        px: '10px',
        fontWeight: 300,
      };

  return (
    <Box as="label" {...htmlProps}>
      <input {...input} disabled={isDisabled} />
      <Box
        {...checkbox}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        {...selectedProps}
        ml={position === 'left' ? '5px' : '0px'}
        mr={position === 'right' ? '5px' : '0px'}
        width="max-content"
        whiteSpace="nowrap"
      >
        <div
          style={{
            width,
            wordBreak: 'keep-all',
          }}
        >
          {children}
        </div>
      </Box>
    </Box>
  );
}
